import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

const SOURCES = ["System", "Canonical Tag", "Smart Rule", "Manual", "Location", "Named Subject", "Legacy / Unknown"];
const FILTERS = ["All", "Valid", "Flagged", ...SOURCES];
const SOURCE_TONE = {
  "System": "bg-sky-500/15 text-sky-300", "Canonical Tag": "bg-emerald-500/15 text-emerald-300", "Smart Rule": "bg-violet-500/15 text-violet-300",
  "Manual": "bg-zinc-500/20 text-zinc-200", "Location": "bg-teal-500/15 text-teal-300", "Named Subject": "bg-pink-500/15 text-pink-300",
  "Legacy / Unknown": "bg-amber-500/20 text-amber-300",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

const SourceRow = ({ r, highlight }) => (
  <tr data-testid={`gallery-source-row-${r.id}`} data-flagged={r.flagged ? "true" : "false"} data-source={r.source}
    className={cn("border-t border-border/60 align-top", r.flagged && "bg-amber-500/[0.07]", highlight && "ring-1 ring-inset ring-amber-400/60")}>
    <td className="px-3 py-2">
      <div className="font-medium text-sm flex items-center gap-1.5">{r.flagged && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" aria-hidden="true" />}<span data-testid={`gallery-source-name-${r.id}`}>{r.name}</span></div>
      <div className="text-[10px] text-muted-foreground font-mono break-all" data-testid={`gallery-source-id-${r.id}`}>{r.id}</div>
    </td>
    <td className="px-3 py-2"><span className={cn("text-[10px] uppercase font-bold px-2 py-0.5 rounded whitespace-nowrap", SOURCE_TONE[r.source] || "bg-secondary")} data-testid={`gallery-source-kind-${r.id}`}>{r.source}</span></td>
    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[260px]" data-testid={`gallery-source-detail-${r.id}`}>{r.source_detail || "—"}</td>
    <td className="px-3 py-2"><span className={cn("text-[10px] uppercase font-bold px-2 py-0.5 rounded", r.flagged ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/15 text-emerald-300")} data-testid={`gallery-source-status-${r.id}`}>{r.status}</span></td>
    <td className="px-3 py-2 text-xs text-right tabular-nums" data-testid={`gallery-source-count-${r.id}`}>{r.count}</td>
    <td className="px-3 py-2 text-xs text-right tabular-nums" data-testid={`gallery-source-mids-${r.id}`}>{r.media_ids_count}</td>
    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmt(r.created_at)}</td>
    <td className="px-3 py-2 text-xs max-w-[280px]" data-testid={`gallery-source-reason-${r.id}`}>
      {r.flagged ? (
        <div className="text-amber-300 space-y-0.5">
          {r.requires_manual_review && <div className="font-bold uppercase text-[10px] tracking-wider" data-testid={`gallery-source-manual-review-${r.id}`}>Requires manual review</div>}
          {r.issues.map((i, k) => <div key={k}>{i}</div>)}
          {r.probable_creation_path && <div className="text-[10px] text-muted-foreground">Likely path: {r.probable_creation_path}</div>}
        </div>
      ) : <span className="text-muted-foreground">—</span>}
    </td>
  </tr>
);

export const GallerySourcePanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [dry, setDry] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const load = useCallback(async () => {
    setError(false);
    try { const r = await api.get("/admin/galleries/registry"); setData(r.data); } catch { setError(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.galleries || [];
    const byFilter = filter === "All" ? all : filter === "Valid" ? all.filter((r) => !r.flagged) : filter === "Flagged" ? all.filter((r) => r.flagged) : all.filter((r) => r.source === filter);
    const s = q.trim().toLowerCase();
    return s ? byFilter.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.source_detail || "").toLowerCase().includes(s) || (r.source || "").toLowerCase().includes(s)) : byFilter;
  }, [data, filter, q]);
  const counts = useMemo(() => {
    const all = data?.galleries || [];
    const c = { All: all.length, Valid: all.filter((r) => !r.flagged).length, Flagged: all.filter((r) => r.flagged).length };
    SOURCES.forEach((s) => { c[s] = all.filter((r) => r.source === s).length; });
    return c;
  }, [data]);
  const dryIds = useMemo(() => new Set((dry?.candidates || []).map((c) => c.id)), [dry]);

  const runDry = async () => {
    setRunning(true);
    try { const r = await api.post("/admin/galleries/registry/cleanup", {}); setDry(r.data); toast.success(`${r.data.rogue_found} rogue galler${r.data.rogue_found === 1 ? "y" : "ies"} found`); }
    catch { toast.error("Dry run failed"); }
    finally { setRunning(false); }
  };
  const execute = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/galleries/registry/cleanup", { confirm: true });
      toast.success(`Deleted ${r.data.deleted} rogue gallery definition${r.data.deleted === 1 ? "" : "s"} · media untouched`);
      setDry(null); setConfirmOpen(false);
      await load();
    } catch { toast.error("Cleanup failed"); }
    finally { setRunning(false); }
  };

  const safeN = dry?.safe_to_delete || 0;
  return (
    <div className="mt-8 border-t border-border pt-6" data-testid="gallery-source-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Gallery Source</div>
          <p className="text-xs text-muted-foreground mt-1">Where every gallery comes from. Only active canonical master tags may own a tag gallery — AI suggestions never create gallery structure.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 rounded-full border-border text-xs" data-testid="gallery-source-refresh" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Refresh</Button>
          <Button size="sm" variant="outline" className="h-8 rounded-full border-amber-400/50 text-amber-300 hover:bg-amber-400/10 text-xs" data-testid="gallery-source-dry-run" onClick={runDry} disabled={running}>
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}Review rogue galleries (dry run)
          </Button>
          {dry && safeN > 0 && (
            <Button size="sm" className="h-8 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs" data-testid="gallery-source-delete-btn" onClick={() => setConfirmOpen(true)} disabled={running}>
              <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Delete {safeN} rogue galler{safeN === 1 ? "y" : "ies"}
            </Button>
          )}
        </div>
      </div>

      {dry && (
        <div className="mb-3 rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 text-sm" data-testid="gallery-source-dry-summary">
          <div className="font-medium text-amber-200">Dry run · nothing was deleted</div>
          <div className="text-xs text-amber-100/90 mt-1 flex flex-wrap gap-x-4">
            <span data-testid="dry-rogue-found">{dry.rogue_found} rogue galler{dry.rogue_found === 1 ? "y" : "ies"} found</span>
            <span data-testid="dry-safe">{dry.safe_to_delete} safe to delete</span>
            <span data-testid="dry-review">{dry.manual_review_required} require{dry.manual_review_required === 1 ? "s" : ""} manual review</span>
            <span className="text-muted-foreground">{dry.galleries_audited} galleries audited</span>
          </div>
          {dry.candidates?.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs" data-testid="dry-candidates">
              {dry.candidates.map((c) => (
                <li key={c.id} data-testid={`dry-candidate-${c.id}`} className="flex flex-wrap gap-x-2">
                  <span className={cn("font-bold uppercase text-[10px] px-1.5 rounded", c.would_delete ? "bg-destructive/30 text-red-200" : "bg-amber-500/30 text-amber-200")}>{c.would_delete ? "would delete" : "manual review"}</span>
                  <span className="font-medium">{c.name}</span><span className="text-muted-foreground">tag: {c.source_tag} · {c.reason} · media_ids {c.media_ids_count}</span>
                </li>
              ))}
            </ul>
          )}
          {dry.rogue_found === 0 && <div className="text-xs text-emerald-300 mt-1" data-testid="dry-clean">No rogue tag galleries — every gallery resolves to a valid source.</div>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="gallery-source-filters">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`gallery-source-filter-${f.replace(/[^a-z]+/gi, "-").toLowerCase()}`} onClick={() => setFilter(f)}
            className={cn("text-[11px] rounded-full px-2.5 py-1 border transition-colors", filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground",
              f === "Flagged" && counts.Flagged > 0 && filter !== f && "border-amber-400/50 text-amber-300")}>
            {f} <span className="opacity-70">{counts[f] ?? 0}</span>
          </button>
        ))}
        <input data-testid="gallery-source-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / source" className="ml-auto bg-secondary/50 border border-border rounded-full px-3 py-1 text-xs outline-none w-48" />
      </div>

      {error ? <div className="text-sm text-destructive" data-testid="gallery-source-error">Couldn't load the gallery registry. <button className="underline" onClick={load}>Retry</button></div>
        : !data ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
        : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left" data-testid="gallery-source-table">
              <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-3 py-2">Gallery</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Source detail</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Items</th><th className="px-3 py-2 text-right">media_ids</th><th className="px-3 py-2">Created</th><th className="px-3 py-2">Flag / reason</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => <SourceRow key={`${r.collection}-${r.id}`} r={r} highlight={dryIds.has(r.id)} />)}
                {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="gallery-source-empty">No galleries match this filter.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      <div className="text-[11px] text-muted-foreground mt-2" data-testid="gallery-source-count">Showing {rows.length} of {data?.total ?? 0} galleries</div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="gallery-source-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {safeN} rogue gallery definition{safeN === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>Only invalid gallery definitions will be deleted. No photos, videos, tags, ratings, comments, or files will be changed. Galleries marked “Requires manual review” are not included.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gallery-source-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="gallery-source-confirm-delete" onClick={(e) => { e.preventDefault(); execute(); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete {safeN} Rogue Galler{safeN === 1 ? "y" : "ies"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
